import { defineMessages } from "../define";

/**
 * Monitoring board, traffic tabs: Requests (monitoring.requests.*) and Errors & events
 * (monitoring.events.*). Shared words (levels, p50/p95/p99, columns) live in monitoring.ts.
 */
export default defineMessages({
  en: {
    // --- requests: live -----------------------------------------------------------------------------
    "monitoring.requests.live.title": "Right now",
    "monitoring.requests.live.description": "Live requests of the instance answering the board ({instance})",
    "monitoring.requests.live.lastMinute": "Requests in the last minute",
    "monitoring.requests.live.capped":
      "The backend keeps the last {size} requests, so the last-minute count cannot go higher",
    "monitoring.requests.live.inFlight": "In flight now",
    "monitoring.requests.live.inFlightDetail": "Requests being answered at this moment",
    "monitoring.requests.live.lastRequest": "Last request",
    "monitoring.requests.live.noRequest": "No request since the backend started",
    "monitoring.requests.live.sinceStart": "Requests since start",
    "monitoring.requests.live.startedAt": "Backend started",

    // --- requests: window totals --------------------------------------------------------------------
    "monitoring.requests.window.title": "Requests in the {window}",
    "monitoring.requests.window.description":
      "All instances of the selected data source, from the stored metrics. The monitoring API itself is not counted.",
    "monitoring.requests.window.requests": "Requests",
    "monitoring.requests.perMinute": "{rate} per minute",
    "monitoring.requests.window.serverErrors": "Server errors (5xx)",
    "monitoring.requests.window.clientErrors": "Client errors (4xx)",
    "monitoring.requests.window.share": "{share} of requests",
    "monitoring.requests.window.exceptions": "{count} unhandled exceptions",
    "monitoring.requests.window.latencyP95": "Latency p95",
    "monitoring.requests.window.latencyDetail": "p50 {p50} · p99 {p99} · max {max}",
    "monitoring.requests.window.maxInFlight": "Max in flight",
    "monitoring.requests.window.maxInFlightDetail": "Most requests answered at once",
    "monitoring.requests.latencyExcludes":
      "Long-poll routes (waiting for the evaluation, camera photo) are left out of the overall latency.",

    // --- requests: status classes -------------------------------------------------------------------
    "monitoring.requests.class.s2": "Success (2xx)",
    "monitoring.requests.class.s3": "Redirect (3xx)",
    "monitoring.requests.class.s4": "Client error (4xx)",
    "monitoring.requests.class.s5": "Server error (5xx)",
    "monitoring.requests.class.short.s2": "2xx",
    "monitoring.requests.class.short.s3": "3xx",
    "monitoring.requests.class.short.s4": "4xx",
    "monitoring.requests.class.short.s5": "5xx",

    // --- requests: charts ---------------------------------------------------------------------------
    "monitoring.requests.chart.volume": "Requests by status",
    "monitoring.requests.chart.volumeDescription": "Requests per interval by status class ({window})",
    "monitoring.requests.chart.failed": "Failed requests",
    "monitoring.requests.chart.failedDescription": "Client errors (4xx) and server errors (5xx) per interval ({window})",
    "monitoring.requests.chart.latency": "Latency",
    "monitoring.requests.chart.latencyDescription": "Response time percentiles per interval ({window})",
    "monitoring.requests.chart.inFlight": "Concurrency",
    "monitoring.requests.chart.inFlightDescription": "Most requests in flight at once per interval ({window})",
    "monitoring.requests.chart.inFlightSeries": "Max in flight",
    "monitoring.requests.chart.noRequests": "No request in this window",
    "monitoring.requests.chart.noFailed": "No failed request in this window",
    "monitoring.requests.chart.slowLine": "slow (2 s)",

    // --- requests: routes ---------------------------------------------------------------------------
    "monitoring.requests.routes.title": "Endpoints",
    "monitoring.requests.routes.description":
      "Every route the API answered ({window}); the 5xx rate is the share of server errors",
    "monitoring.requests.routes.search": "Filter routes…",
    "monitoring.requests.routes.sortBy": "Sort by",
    "monitoring.requests.routes.sort.requests": "Requests",
    "monitoring.requests.routes.sort.errors": "Errors",
    "monitoring.requests.routes.sort.p95": "p95",
    "monitoring.requests.routes.sort.max": "Max",
    "monitoring.requests.routes.sorted": "sorted",
    "monitoring.requests.routes.noMatch": "No route matches the filter",
    "monitoring.requests.routes.showAll": "Show all {count} routes",
    "monitoring.requests.routes.showFewer": "Show the first {count}",
    "monitoring.requests.col.route": "Route",
    "monitoring.requests.col.requests": "Requests",
    "monitoring.requests.col.perMinute": "Per min",
    "monitoring.requests.col.errorRate": "5xx rate",
    "monitoring.requests.col.errorRateHint": "Share of requests answered with a server error (5xx)",
    "monitoring.requests.col.max": "Max",
    "monitoring.requests.col.trend": "Trend",
    "monitoring.requests.col.method": "Method",
    "monitoring.requests.col.path": "Path",
    "monitoring.requests.col.status": "Status",
    "monitoring.requests.col.duration": "Duration",
    "monitoring.requests.col.client": "Client IP",
    "monitoring.requests.col.device": "Device",
    "monitoring.requests.longPoll": "long poll",
    "monitoring.requests.longPollHint":
      "Waits by design (long poll or on-camera capture): counted, but kept out of the overall latency and the slow list",

    // --- requests: slow -----------------------------------------------------------------------------
    "monitoring.requests.slow.title": "Slow requests (≥ 2 s)",
    "monitoring.requests.slow.description":
      "The latest requests of this instance that took 2 s or more, newest first (long-poll routes excluded)",
    "monitoring.requests.slow.empty": "No request took 2 s or more since the backend started.",
    "monitoring.requests.slow.showAll": "Show all {count}",

    // --- requests: live tail ------------------------------------------------------------------------
    "monitoring.requests.tail.title": "Latest requests",
    "monitoring.requests.tail.description": "The last {count} requests answered by this instance, newest first",
    "monitoring.requests.tail.filter": "Status filter",
    "monitoring.requests.tail.filter.all": "All",
    "monitoring.requests.tail.filter.errors": "Errors",
    "monitoring.requests.tail.filter.s4": "4xx",
    "monitoring.requests.tail.filter.s5": "5xx",
    "monitoring.requests.tail.search": "Filter by path, IP, device, status…",
    "monitoring.requests.tail.freeze": "Freeze",
    "monitoring.requests.tail.frozen": "Frozen",
    "monitoring.requests.tail.freezeHint": "Keep the list still while you read it (updates continue in the background)",
    "monitoring.requests.tail.frozenAt": "Frozen at {time}",
    "monitoring.requests.tail.newer": "{count} newer requests not shown",
    "monitoring.requests.tail.newerMany": "{count}+ newer requests not shown",
    "monitoring.requests.tail.showLatest": "Show the latest",
    "monitoring.requests.tail.empty": "No request since the backend started.",
    "monitoring.requests.tail.noMatch": "No request matches the filter",
    "monitoring.requests.tail.openCamera": "Open camera {device}",

    // --- events: shared -----------------------------------------------------------------------------
    "monitoring.events.copy": "Copy {what}",
    "monitoring.events.foldedHint":
      "Repeats of the same event within a minute are folded into one entry with a count",
    "monitoring.events.source.http": "API",
    "monitoring.events.source.pod_pool": "GPU pods",
    "monitoring.events.source.camera": "Cameras",
    "monitoring.events.source.thingsboard": "ThingsBoard",
    "monitoring.events.source.evaluation": "Evaluation",
    "monitoring.events.source.storage": "Storage (S3)",
    "monitoring.events.source.biteberry": "BiteBerry",
    "monitoring.events.source.backend": "Backend",
    "monitoring.events.source.push": "Push notifications",

    // --- events: overview ---------------------------------------------------------------------------
    "monitoring.events.overview.title": "Events in the {window}",
    "monitoring.events.overview.description": "Every occurrence counts, folded repeats included",
    "monitoring.events.overview.errors": "Errors",
    "monitoring.events.overview.warnings": "Warnings",
    "monitoring.events.overview.infos": "Info",
    "monitoring.events.overview.perHour": "{rate} per hour",
    "monitoring.events.overview.empty": "No event in this window",

    // --- events: groups -----------------------------------------------------------------------------
    "monitoring.events.groups.title": "Recurring problems",
    "monitoring.events.groups.description":
      "Events grouped by source, code and subject ({window}), the most frequent first. Click a group to see its events in the log.",
    "monitoring.events.groups.levels": "Levels of the groups",
    "monitoring.events.groups.empty": "No problem recorded in this window.",
    "monitoring.events.groups.memory":
      "Grouping needs the stored events, and monitoring persistence is off on this backend.",
    "monitoring.events.groups.first": "first",
    "monitoring.events.groups.last": "last",
    "monitoring.events.groups.pizzas": "{pizzas} affected",
    "monitoring.events.groups.stored": "{events} entries + {repeats} folded repeats",
    "monitoring.events.groups.occurrences": "occurrences",
    "monitoring.events.groups.ongoing": "ongoing",
    "monitoring.events.groups.showInLog": "Show in the event log",
    "monitoring.events.groups.showAll": "Show all {count} groups",
    "monitoring.events.groups.showFewer": "Show the first {count}",

    // --- events: log --------------------------------------------------------------------------------
    "monitoring.events.log.title": "Event log",
    "monitoring.events.log.description": "Errors, warnings and state changes of the backend, newest first",
    "monitoring.events.log.levels": "Levels",
    "monitoring.events.log.source": "Source",
    "monitoring.events.log.allSources": "All sources",
    "monitoring.events.log.range": "Time range",
    "monitoring.events.log.limit": "Limit",
    "monitoring.events.log.search": "Search message, code or subject…",
    "monitoring.events.log.pizzaId": "Pizza id",
    "monitoring.events.log.reset": "Reset filters",
    "monitoring.events.log.group": "Group",
    "monitoring.events.log.clearGroup": "Show all events again",
    "monitoring.events.log.count.one": "{count} event",
    "monitoring.events.log.count.few": "{count} events",
    "monitoring.events.log.count.other": "{count} events",
    "monitoring.events.log.since": "since {time}",
    "monitoring.events.log.limitReached":
      "Showing the newest {limit}: narrow the filters or raise the limit to see older ones",
    "monitoring.events.log.memory":
      "Monitoring persistence is off: the log is the memory of the instance answering the board (its last {size} events). Other instances are not included and the filters run in the browser.",
    "monitoring.events.log.empty": "No event in this time range.",
    "monitoring.events.log.noMatch": "No event matches the filters.",
    "monitoring.events.log.expand": "Show details",
    "monitoring.events.log.collapse": "Hide details",
    "monitoring.events.log.until": "until {time}",
    "monitoring.events.log.openPizza": "Open pizza {id}",
    "monitoring.events.log.openCamera": "Open camera {id}",
    "monitoring.events.log.instanceHint": "Backend instance ({role})",
    "monitoring.events.log.showMore": "Show {count} more ({left} left)",
    "monitoring.events.range.window": "Selected window ({window})",
    "monitoring.events.range.15m": "Last 15 minutes",
    "monitoring.events.range.1h": "Last hour",
    "monitoring.events.range.6h": "Last 6 hours",
    "monitoring.events.range.24h": "Last 24 hours",
    "monitoring.events.range.7d": "Last 7 days",
    "monitoring.events.range.30d": "Last 30 days (all kept)",

    // --- events: details ----------------------------------------------------------------------------
    "monitoring.events.detail.time": "Time",
    "monitoring.events.detail.lastRepeat": "Last repeat",
    "monitoring.events.detail.occurrences": "Occurrences",
    "monitoring.events.detail.fingerprint": "Fingerprint",
    "monitoring.events.detail.device": "Camera",
    "monitoring.events.detail.instance": "Instance",
    "monitoring.events.detail.id": "Event id",
    "monitoring.events.detail.details": "Details",
    "monitoring.events.detail.traceback": "Traceback",
    "monitoring.events.detail.nothingMore": "No details or traceback recorded.",
  },
  cs: {
    "monitoring.requests.live.title": "Právě teď",
    "monitoring.requests.live.description": "Živé požadavky instance, která boardu odpovídá ({instance})",
    "monitoring.requests.live.lastMinute": "Požadavky za poslední minutu",
    "monitoring.requests.live.capped":
      "Backend drží posledních {size} požadavků, počet za minutu proto nemůže být vyšší",
    "monitoring.requests.live.inFlight": "Právě zpracovávané",
    "monitoring.requests.live.inFlightDetail": "Požadavky, na které se v tuto chvíli odpovídá",
    "monitoring.requests.live.lastRequest": "Poslední požadavek",
    "monitoring.requests.live.noRequest": "Od startu backendu žádný požadavek",
    "monitoring.requests.live.sinceStart": "Požadavky od startu",
    "monitoring.requests.live.startedAt": "Backend spuštěn",

    "monitoring.requests.window.title": "Požadavky ({window})",
    "monitoring.requests.window.description":
      "Všechny instance zvoleného zdroje dat, z uložených metrik. Samotné API monitoringu se nepočítá.",
    "monitoring.requests.window.requests": "Požadavky",
    "monitoring.requests.perMinute": "{rate} za minutu",
    "monitoring.requests.window.serverErrors": "Chyby serveru (5xx)",
    "monitoring.requests.window.clientErrors": "Chyby klienta (4xx)",
    "monitoring.requests.window.share": "{share} požadavků",
    "monitoring.requests.window.exceptions": "neošetřené výjimky: {count}",
    "monitoring.requests.window.latencyP95": "Latence p95",
    "monitoring.requests.window.latencyDetail": "p50 {p50} · p99 {p99} · max {max}",
    "monitoring.requests.window.maxInFlight": "Nejvíc najednou",
    "monitoring.requests.window.maxInFlightDetail": "Nejvíc požadavků zpracovávaných současně",
    "monitoring.requests.latencyExcludes":
      "Routy s dlouhým čekáním (čekání na vyhodnocení, fotka z kamery) se do celkové latence nepočítají.",

    "monitoring.requests.class.s2": "Úspěch (2xx)",
    "monitoring.requests.class.s3": "Přesměrování (3xx)",
    "monitoring.requests.class.s4": "Chyba klienta (4xx)",
    "monitoring.requests.class.s5": "Chyba serveru (5xx)",
    "monitoring.requests.class.short.s2": "2xx",
    "monitoring.requests.class.short.s3": "3xx",
    "monitoring.requests.class.short.s4": "4xx",
    "monitoring.requests.class.short.s5": "5xx",

    "monitoring.requests.chart.volume": "Požadavky podle stavu",
    "monitoring.requests.chart.volumeDescription": "Požadavky za interval podle třídy stavového kódu ({window})",
    "monitoring.requests.chart.failed": "Neúspěšné požadavky",
    "monitoring.requests.chart.failedDescription": "Chyby klienta (4xx) a chyby serveru (5xx) za interval ({window})",
    "monitoring.requests.chart.latency": "Latence",
    "monitoring.requests.chart.latencyDescription": "Percentily doby odpovědi za interval ({window})",
    "monitoring.requests.chart.inFlight": "Souběžnost",
    "monitoring.requests.chart.inFlightDescription": "Nejvíc současně zpracovávaných požadavků za interval ({window})",
    "monitoring.requests.chart.inFlightSeries": "Nejvíc najednou",
    "monitoring.requests.chart.noRequests": "V tomto období žádný požadavek",
    "monitoring.requests.chart.noFailed": "V tomto období žádný neúspěšný požadavek",
    "monitoring.requests.chart.slowLine": "pomalé (2 s)",

    "monitoring.requests.routes.title": "Endpointy",
    "monitoring.requests.routes.description":
      "Všechny routy, na které API odpovídalo ({window}); podíl 5xx je podíl chyb serveru",
    "monitoring.requests.routes.search": "Filtrovat routy…",
    "monitoring.requests.routes.sortBy": "Řadit podle",
    "monitoring.requests.routes.sort.requests": "Požadavky",
    "monitoring.requests.routes.sort.errors": "Chyby",
    "monitoring.requests.routes.sort.p95": "p95",
    "monitoring.requests.routes.sort.max": "Max",
    "monitoring.requests.routes.sorted": "seřazeno",
    "monitoring.requests.routes.noMatch": "Filtru neodpovídá žádná routa",
    "monitoring.requests.routes.showAll": "Zobrazit všechny routy ({count})",
    "monitoring.requests.routes.showFewer": "Zobrazit prvních {count}",
    "monitoring.requests.col.route": "Routa",
    "monitoring.requests.col.requests": "Požadavky",
    "monitoring.requests.col.perMinute": "Za min",
    "monitoring.requests.col.errorRate": "Podíl 5xx",
    "monitoring.requests.col.errorRateHint": "Podíl požadavků, na které server odpověděl chybou (5xx)",
    "monitoring.requests.col.max": "Max",
    "monitoring.requests.col.trend": "Průběh",
    "monitoring.requests.col.method": "Metoda",
    "monitoring.requests.col.path": "Cesta",
    "monitoring.requests.col.status": "Stav",
    "monitoring.requests.col.duration": "Doba",
    "monitoring.requests.col.client": "IP klienta",
    "monitoring.requests.col.device": "Zařízení",
    "monitoring.requests.longPoll": "long poll",
    "monitoring.requests.longPollHint":
      "Čeká záměrně (long poll nebo focení na kameře): počítá se, ale ne do celkové latence ani do seznamu pomalých",

    "monitoring.requests.slow.title": "Pomalé požadavky (≥ 2 s)",
    "monitoring.requests.slow.description":
      "Poslední požadavky této instance, které trvaly 2 s nebo déle, nejnovější první (bez rout s dlouhým čekáním)",
    "monitoring.requests.slow.empty": "Od startu backendu netrval žádný požadavek 2 s ani déle.",
    "monitoring.requests.slow.showAll": "Zobrazit všechny ({count})",

    "monitoring.requests.tail.title": "Poslední požadavky",
    "monitoring.requests.tail.description": "Posledních {count} požadavků, na které tato instance odpověděla, nejnovější první",
    "monitoring.requests.tail.filter": "Filtr stavu",
    "monitoring.requests.tail.filter.all": "Vše",
    "monitoring.requests.tail.filter.errors": "Chyby",
    "monitoring.requests.tail.filter.s4": "4xx",
    "monitoring.requests.tail.filter.s5": "5xx",
    "monitoring.requests.tail.search": "Filtrovat podle cesty, IP, zařízení, stavu…",
    "monitoring.requests.tail.freeze": "Zmrazit",
    "monitoring.requests.tail.frozen": "Zmrazeno",
    "monitoring.requests.tail.freezeHint": "Podrží seznam v klidu, než ho dočtete (aktualizace běží dál na pozadí)",
    "monitoring.requests.tail.frozenAt": "Zmrazeno v {time}",
    "monitoring.requests.tail.newer": "novější požadavky, které nejsou zobrazené: {count}",
    "monitoring.requests.tail.newerMany": "novější požadavky, které nejsou zobrazené: {count}+",
    "monitoring.requests.tail.showLatest": "Zobrazit nejnovější",
    "monitoring.requests.tail.empty": "Od startu backendu žádný požadavek.",
    "monitoring.requests.tail.noMatch": "Filtru neodpovídá žádný požadavek",
    "monitoring.requests.tail.openCamera": "Otevřít kameru {device}",

    "monitoring.events.copy": "Kopírovat: {what}",
    "monitoring.events.foldedHint":
      "Opakování stejné události během minuty se slučují do jednoho záznamu s počtem",
    "monitoring.events.source.http": "API",
    "monitoring.events.source.pod_pool": "GPU pody",
    "monitoring.events.source.camera": "Kamery",
    "monitoring.events.source.thingsboard": "ThingsBoard",
    "monitoring.events.source.evaluation": "Vyhodnocení",
    "monitoring.events.source.storage": "Úložiště (S3)",
    "monitoring.events.source.biteberry": "BiteBerry",
    "monitoring.events.source.backend": "Backend",
    "monitoring.events.source.push": "Push notifikace",

    "monitoring.events.overview.title": "Události ({window})",
    "monitoring.events.overview.description": "Počítá se každý výskyt včetně sloučených opakování",
    "monitoring.events.overview.errors": "Chyby",
    "monitoring.events.overview.warnings": "Varování",
    "monitoring.events.overview.infos": "Info",
    "monitoring.events.overview.perHour": "{rate} za hodinu",
    "monitoring.events.overview.empty": "V tomto období žádná událost",

    "monitoring.events.groups.title": "Opakující se problémy",
    "monitoring.events.groups.description":
      "Události seskupené podle zdroje, kódu a předmětu ({window}), nejčastější první. Kliknutím na skupinu zobrazíte její události v logu.",
    "monitoring.events.groups.levels": "Úrovně skupin",
    "monitoring.events.groups.empty": "V tomto období nebyl zaznamenán žádný problém.",
    "monitoring.events.groups.memory":
      "Seskupení potřebuje uložené události a ukládání monitoringu je na tomto backendu vypnuté.",
    "monitoring.events.groups.first": "poprvé",
    "monitoring.events.groups.last": "naposledy",
    "monitoring.events.groups.pizzas": "{pizzas} s problémem",
    "monitoring.events.groups.stored": "záznamy: {events}, sloučená opakování: {repeats}",
    "monitoring.events.groups.occurrences": "výskytů",
    "monitoring.events.groups.ongoing": "probíhá",
    "monitoring.events.groups.showInLog": "Zobrazit v logu událostí",
    "monitoring.events.groups.showAll": "Zobrazit všechny skupiny ({count})",
    "monitoring.events.groups.showFewer": "Zobrazit prvních {count}",

    "monitoring.events.log.title": "Log událostí",
    "monitoring.events.log.description": "Chyby, varování a změny stavu backendu, nejnovější první",
    "monitoring.events.log.levels": "Úrovně",
    "monitoring.events.log.source": "Zdroj",
    "monitoring.events.log.allSources": "Všechny zdroje",
    "monitoring.events.log.range": "Časový rozsah",
    "monitoring.events.log.limit": "Limit",
    "monitoring.events.log.search": "Hledat ve zprávě, kódu nebo předmětu…",
    "monitoring.events.log.pizzaId": "ID pizzy",
    "monitoring.events.log.reset": "Zrušit filtry",
    "monitoring.events.log.group": "Skupina",
    "monitoring.events.log.clearGroup": "Zobrazit znovu všechny události",
    "monitoring.events.log.count.one": "{count} událost",
    "monitoring.events.log.count.few": "{count} události",
    "monitoring.events.log.count.other": "{count} událostí",
    "monitoring.events.log.since": "od {time}",
    "monitoring.events.log.limitReached":
      "Zobrazeno nejnovějších {limit}: pro starší zužte filtry nebo zvyšte limit",
    "monitoring.events.log.memory":
      "Ukládání monitoringu je vypnuté: log je paměť instance, která boardu odpovídá (jejích posledních {size} událostí). Ostatní instance chybí a filtry běží v prohlížeči.",
    "monitoring.events.log.empty": "V tomto časovém rozsahu žádná událost.",
    "monitoring.events.log.noMatch": "Filtrům neodpovídá žádná událost.",
    "monitoring.events.log.expand": "Zobrazit detail",
    "monitoring.events.log.collapse": "Skrýt detail",
    "monitoring.events.log.until": "do {time}",
    "monitoring.events.log.openPizza": "Otevřít pizzu {id}",
    "monitoring.events.log.openCamera": "Otevřít kameru {id}",
    "monitoring.events.log.instanceHint": "Instance backendu ({role})",
    "monitoring.events.log.showMore": "Zobrazit dalších {count} (zbývá {left})",
    "monitoring.events.range.window": "Zvolené období ({window})",
    "monitoring.events.range.15m": "Posledních 15 minut",
    "monitoring.events.range.1h": "Poslední hodina",
    "monitoring.events.range.6h": "Posledních 6 hodin",
    "monitoring.events.range.24h": "Posledních 24 hodin",
    "monitoring.events.range.7d": "Posledních 7 dní",
    "monitoring.events.range.30d": "Posledních 30 dní (vše uložené)",

    "monitoring.events.detail.time": "Čas",
    "monitoring.events.detail.lastRepeat": "Poslední opakování",
    "monitoring.events.detail.occurrences": "Výskyty",
    "monitoring.events.detail.fingerprint": "Otisk",
    "monitoring.events.detail.device": "Kamera",
    "monitoring.events.detail.instance": "Instance",
    "monitoring.events.detail.id": "ID události",
    "monitoring.events.detail.details": "Detaily",
    "monitoring.events.detail.traceback": "Traceback",
    "monitoring.events.detail.nothingMore": "Žádné detaily ani traceback nebyly zaznamenány.",
  },
});
